import { useState, useEffect, useCallback } from 'react';
import * as Location from 'expo-location';
import { ensureForegroundLocationPermission } from '@/utils/permissions';
import { getBestAvailableLocation } from '@/utils/location';
import { useTranslation } from 'react-i18next';

/**
 * Hook that resolves and caches the user's current location.
 * Returns:
 *  - locationCoords: { latitude, longitude } or undefined
 *  - locationLabel: human-readable string ("Current location" / "Location unavailable")
 *  - isLoading: true while resolving location
 *  - refresh: re-request location
 */
export function useUserLocation() {
  const { t } = useTranslation();
  const [locationCoords, setLocationCoords] = useState<{ latitude: number; longitude: number } | undefined>(undefined);
  const [locationLabel, setLocationLabel] = useState<string>(t('explore.locationUnavailable'));
  const [isLoading, setIsLoading] = useState(true);

  const resolve = useCallback(async () => {
    setIsLoading(true);
    try {
      const granted = await ensureForegroundLocationPermission();
      if (granted) {
        const location = await getBestAvailableLocation({ accuracy: Location.Accuracy.Balanced });
        if (location) {
          setLocationCoords({ latitude: location.coords.latitude, longitude: location.coords.longitude });
          setLocationLabel(t('explore.currentLocation'));
        } else {
          setLocationCoords(undefined);
          setLocationLabel(t('explore.locationUnavailable'));
        }
      } else {
        setLocationCoords(undefined);
        setLocationLabel(t('explore.locationUnavailable'));
      }
    } catch {
      setLocationCoords(undefined);
      setLocationLabel(t('explore.locationUnavailable'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  useEffect(() => {
    resolve();
  }, [resolve]);

  return { locationCoords, locationLabel, isLoading, refresh: resolve };
}